import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";
import { manilaRangeStart } from "./cash.queue";
import { toCashRetention } from "./cash.retention";

/**
 * Cash remittance data layer — the 3-stage flow of Business Logic §10 /
 * PRD §6.4: pending → received (Hub Manager) → collected (Main Office Admin).
 *
 * Reads are plain RLS selects: crem_hub_read scopes a hub_manager to their
 * hub regardless of the filter; admin/superadmin read every hub (0011).
 * Stage TIMESTAMPS are not columns on cash_remittances — the when of each
 * transition lives in audit_log (superadmin-only), so the UI shows stage +
 * who, never an invented "received at" time.
 */
export function useRemittancesOverview(hubId: string | null, days: number) {
  return useQuery({
    queryKey: ["remittances-overview", hubId, days],
    queryFn: async () => {
      const since = manilaRangeStart(days);

      // staff has three FKs into cash_remittances (therapist/received_by/
      // collected_by) — every staff embed must name its constraint.
      let query = supabase
        .from("cash_remittances")
        .select(
          // 0166 adds a FOURTH staff FK (`recipient_rider_id`) and the three
          // hand-over timestamps. Without them "pending" is ambiguous on this
          // screen: it now covers both "the therapist still has it" and "a
          // rider is carrying it to you", which are different problems for the
          // person reading this table.
          // `booking_id` (not just the embedded ref) so the Cash Handling
          // split can be looked up per row — CASH's "balance update".
          `id, booking_id, amount, status, created_at, hub_id,
           recipient_kind, recipient_rider_id, instructed_at, transferred_at, rider_confirmed_at,
           hubs ( name ),
           bookings ( ref ),
           therapist:staff!cash_remittances_therapist_id_fkey ( full_name ),
           recipient:staff!cash_remittances_recipient_rider_id_fkey ( full_name ),
           receiver:staff!cash_remittances_received_by_fkey ( full_name ),
           collector:staff!cash_remittances_collected_by_fkey ( full_name )`,
        )
        // Open work at ANY age, history inside the range — see
        // `isOpenRemittance`. The page splits the two back apart by date.
        .or(`status.in.(pending,received),created_at.gte."${since}"`)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (hubId) query = query.eq("hub_id", hubId);

      const { data, error } = await query;
      if (error) throw error;
      return { rows: data, since };
    },
    refetchInterval: 15_000,
  });
}

export type RemittanceRow = NonNullable<
  ReturnType<typeof useRemittancesOverview>["data"]
>["rows"][number];

const REMITTANCE_KEYS = [
  ["remittances-overview"],
  ["cash-remittances"], // hub dashboard money flow
  ["cash-remittances-all"], // command-center cash summary + feed
] as const;

function useInvalidateRemittances() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of REMITTANCE_KEYS) queryClient.invalidateQueries({ queryKey: [...key] });
  };
}

/**
 * Stage transitions carry an audit obligation, so they go through the cash
 * Edge Function, never a direct table write — cash_remittances only grants
 * SELECT to interactive roles (0011). Role gates mirror the function's own
 * withAuth lists (cash/index.ts).
 *
 * Received is the hub manager of that hub confirming cash a therapist handed
 * STRAIGHT to the hub — see `hubReceiptState`. Everything a rider carries in
 * is received on its handoff instead (`useConfirmCashHandoffCollected`).
 */
export function useMarkRemittanceReceived() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (remittanceId: string) => {
      return callEdgeFunction(`cash/remittances/${remittanceId}/receive`);
    },
    onSuccess: invalidate,
  });
}

/** Main office only — never the hub (PRD §6.4, matches the Edge Function's own role gate). */
export function useMarkRemittanceCollected() {
  const invalidate = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (remittanceId: string) => {
      return callEdgeFunction(`cash/remittances/${remittanceId}/collect`);
    },
    onSuccess: invalidate,
  });
}

/**
 * Cash Handling handoffs — CASH § Visibility: "Admin and Hub Manager may see the
 * complete Cash Handling amount, status, confirmations, issue report, balance
 * update, and audit information within their access scope."
 *
 * ⚠ A DIFFERENT TABLE FROM THE ONE ABOVE, AND THE REASON MATTERS. A remittance
 * is one booking's excess cash; a handoff is one leg of its journey (0179), and
 * the third leg is the Hub receiving it. Until now this screen read only
 * remittances, so the Rider→next-Therapist and Rider→Hub legs existed in the
 * database, were confirmable over HTTP, and appeared on nobody's screen.
 *
 * Reads are plain RLS selects: `cash_handoff_hub_read` scopes a hub_manager to
 * their own hub and `cash_handoff_office_read` gives admin/executive/superadmin
 * every hub (0179). The hubId filter is a convenience, never the boundary.
 */
export function useCashHandoffs(hubId: string | null, days: number) {
  return useQuery({
    queryKey: ["cash-handoffs", hubId, days],
    queryFn: async () => {
      const since = manilaRangeStart(days);

      // Two staff FKs into one table, so both embeds name their constraint.
      let query = supabase
        .from("cash_handoffs")
        .select(
          `id, remittance_id, seq, amount, status, hub_id, payer_kind, recipient_kind,
           paid_at, collected_at, issue_reported_at, created_at,
           hubs ( name ),
           payer:staff!cash_handoffs_payer_staff_id_fkey ( full_name ),
           recipient:staff!cash_handoffs_recipient_staff_id_fkey ( full_name ),
           cash_remittances ( bookings ( ref ) )`,
        )
        // Open legs at any age (`isOpenHandoff`), closed ones inside the range.
        .or(`status.in.(awaiting_payment,pending_confirmation),created_at.gte."${since}"`)
        .order("created_at", { ascending: false })
        .limit(500);
      if (hubId) query = query.eq("hub_id", hubId);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type CashHandoffRow = NonNullable<ReturnType<typeof useCashHandoffs>["data"]>[number];

/**
 * Confirm Cash Collected on the Hub leg.
 *
 * ⚠ HUB MANAGER ONLY, AND NOT BECAUSE OF THIS HOOK. `confirm_cash_handoff_
 * collected` (0180) requires a hub_manager whose `hub_id` matches the handoff,
 * because "a Hub has no staff row of its own" — an admin who can see every hub
 * still cannot count cash at one. The Edge route's withAuth list says the same.
 * The screen mirrors it so the button is absent rather than certain to fail.
 */
export function useConfirmCashHandoffCollected() {
  const queryClient = useQueryClient();
  const invalidateRemittances = useInvalidateRemittances();
  return useMutation({
    mutationFn: async (handoffId: string) => {
      return callEdgeFunction(`cash/handoffs/${handoffId}/collect`);
    },
    onSuccess: () => {
      // Collecting the last leg moves custody, so both views are stale.
      queryClient.invalidateQueries({ queryKey: ["cash-handoffs"] });
      invalidateRemittances();
    },
  });
}

/**
 * The Cash Handling split behind one remittance — CASH's "balance update",
 * stored by `0203` in `therapist_cash_retentions`.
 *
 * ⚠ GATED, NOT GUARDED BY THE UI ALONE. `0203` grants this table to therapist,
 * admin, executive and superadmin — a hub_manager select raises 42501, not an
 * empty list. `enabled` keeps that request from ever being made, and the caller
 * hides the trigger for the same roles; the GRANT is the control and both of
 * these are just good manners about it. See `cash.retention.ts` on why the hub
 * manager is excluded and why that is not fixed here.
 *
 * ⚠ NO ROW IS A LEGITIMATE ANSWER, NOT AN ERROR. Remittances opened before 0203
 * have no retention — that migration deliberately shipped with no backfill,
 * because restating settled cash from a new formula is how a ledger stops
 * describing what happened. The dialog says so rather than showing zeroes.
 */
export function useCashRetention(bookingId: string | null, allowed: boolean) {
  return useQuery({
    queryKey: ["cash-retention", bookingId],
    enabled: Boolean(bookingId) && allowed,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("therapist_cash_retentions")
        .select(
          "booking_id, final_cash_collected, service_earnings, surcharge_share, available_before, retained_php, cash_to_pay",
        )
        .eq("booking_id", bookingId as string)
        .maybeSingle();
      if (error) throw error;
      return data ? toCashRetention(data as Record<string, unknown>) : null;
    },
    // Append-only and written once at Cash Received (0203) — it cannot change
    // under the dialog, so there is nothing to poll for.
    staleTime: Infinity,
  });
}
